import React, { useState, useEffect, useRef } from 'react';
import { collection, query, getDocs, deleteDoc, doc, Timestamp, orderBy, where } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useApp } from '../App';
import { MediaContexts, MediaServer } from '../utils/constants';
import { uploadMediaFile } from '../utils/mediaUpload';

// iOS에서 저장한 http URL을 웹용 https URL로 변환
const toWebUrl = (url) => {
  if (!url) return url;
  return url.replace('http://122.45.112.83:5000', MediaServer.baseURL);
};

export default function MediaPanel() {
  const { currentTeam, isStaff, userProfile } = useApp();
  const canEdit = isStaff;   // 남의 미디어 삭제 = 운영진 (W1-01)
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFileName, setUploadFileName] = useState('');
  const [uploadFileIndex, setUploadFileIndex] = useState({ current: 0, total: 0 });
  const [contextFilter, setContextFilter] = useState('all');
  const fileInputRef = useRef(null);

  useEffect(() => { if (currentTeam) loadMedia(); }, [currentTeam, contextFilter]);

  const loadMedia = async () => {
    setLoading(true);
    try {
      let q;
      if (contextFilter === 'all') {
        q = query(collection(db, 'teams', currentTeam.id, 'media'), orderBy('createdAt', 'desc'));
      } else {
        q = query(collection(db, 'teams', currentTeam.id, 'media'), where('context', '==', contextFilter), orderBy('createdAt', 'desc'));
      }
      const snap = await getDocs(q);
      setItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setUploadFileIndex({ current: 0, total: files.length });
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const isVideo = file.type.startsWith('video/');
      const isPhoto = file.type.startsWith('image/');
      if (!isVideo && !isPhoto) { alert('지원하지 않는 파일: ' + file.name); continue; }
      try {
        setUploadProgress(0);
        setUploadFileName(file.name);
        setUploadFileIndex({ current: i + 1, total: files.length });

        await uploadMediaFile(file, currentTeam.id, {
          context: 'general',
          contextId: null,
          uploaderName: userProfile?.name || auth.currentUser?.email,
          onProgress: (pct) => setUploadProgress(pct),
        });
      } catch (err) { alert('업로드 실패 (' + file.name + '): ' + err.message); }
    }
    setUploading(false); setUploadProgress(0); setUploadFileName(''); loadMedia();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('이 미디어를 삭제하시겠습니까?')) return;
    try {
      await deleteDoc(doc(db, 'teams', currentTeam.id, 'media', id));
      setItems(prev => prev.filter(i => i.id !== id));
      if (selected?.id === id) setSelected(null);
    } catch (err) { alert('삭제 실패'); }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + 'KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + 'MB';
    return (bytes / (1024 * 1024 * 1024)).toFixed(2) + 'GB';
  };

  return (
    <div style={{ maxWidth: 1200 }}>
      <div style={s.panel}>
        <div style={s.panelHeader}>
          <h2 style={s.panelTitle}>📷 미디어 갤러리</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select style={s.select} value={contextFilter} onChange={e => setContextFilter(e.target.value)}>
              <option value="all">전체</option>
              {Object.entries(MediaContexts).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <button onClick={() => fileInputRef.current?.click()} style={s.uploadBtn} disabled={uploading}>
              {uploading ? `업로드 중 ${uploadProgress}%` : '📤 파일 업로드'}
            </button>
            <input ref={fileInputRef} type="file" multiple accept="image/*,video/*" style={{ display: 'none' }}
              onChange={e => handleUpload(e.target.files)} />
          </div>
        </div>

        {uploading && (
          <div style={{ padding: '0 20px 12px' }}>
            <div style={{ fontSize: 11, color: '#AAB4C0', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              📄 {uploadFileName} ({uploadFileIndex.current}/{uploadFileIndex.total})
            </div>
            <div style={{ height: 4, backgroundColor: 'rgba(91,196,216,0.1)', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: uploadProgress + '%', backgroundColor: '#5BC4D8', borderRadius: 2, transition: 'width 0.3s' }} />
            </div>
          </div>
        )}

        <div style={{ padding: 16 }}>
          {loading ? <div style={s.empty}>로딩 중...</div> :
            items.length === 0 ? (
              <div style={s.empty}>
                <div style={{ fontSize: 48, marginBottom: 12, opacity: 0.5 }}>📷</div>
                <div>미디어가 없습니다</div>
                <div style={{ fontSize: 12, color: '#445566', marginTop: 8 }}>사진이나 영상 파일을 업로드하세요 (드래그&드롭 지원 예정)</div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
                {items.map(item => (
                  <div key={item.id} onClick={() => setSelected(selected?.id === item.id ? null : item)} style={{
                    borderRadius: 8, overflow: 'hidden', cursor: 'pointer', position: 'relative',
                    border: selected?.id === item.id ? '2px solid #5BC4D8' : '2px solid transparent',
                  }}>
                    {item.type === 'photo' ? (
                      <img src={toWebUrl(item.thumbnailUrl || item.url)} alt={item.fileName} style={{ width: '100%', height: 160, objectFit: 'cover', display: 'block', backgroundColor: '#0d1e2d' }} />
                    ) : (
                      item.thumbnailUrl ? (
                        <div style={{ position: 'relative', width: '100%', height: 160 }}>
                          <img src={toWebUrl(item.thumbnailUrl)} alt={item.fileName} style={{ width: '100%', height: 160, objectFit: 'cover', display: 'block', backgroundColor: '#0d1e2d' }} />
                          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', fontSize: 28, opacity: 0.85, textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>▶</div>
                          {item.duration && <span style={{ position: 'absolute', bottom: 4, right: 6, fontSize: 10, color: '#fff', backgroundColor: 'rgba(0,0,0,0.6)', padding: '1px 5px', borderRadius: 3 }}>{Math.floor(item.duration/60)}:{String(Math.floor(item.duration%60)).padStart(2,'0')}</span>}
                        </div>
                      ) : (
                        <div style={{ width: '100%', height: 160, backgroundColor: '#0d1e2d', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={{ fontSize: 40 }}>🎬</span>
                        </div>
                      )
                    )}
                    <div style={{ padding: '8px 10px', backgroundColor: 'rgba(255,255,255,0.02)' }}>
                      <div style={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.fileName}</div>
                      <div style={{ fontSize: 11, color: '#667788', marginTop: 2 }}>
                        {formatSize(item.fileSize)} · {item.uploaderName || ''}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
        </div>
      </div>

      {/* Selected detail */}
      {selected && (
        <div style={{ ...s.panel, marginTop: 16 }}>
          <div style={s.panelHeader}>
            <h2 style={s.panelTitle}>📋 미디어 상세</h2>
            <button onClick={() => setSelected(null)} style={s.closeBtn}>✕</button>
          </div>
          <div style={{ padding: 20, display: 'grid', gridTemplateColumns: '1fr 300px', gap: 20 }}>
            <div>
              {selected.type === 'photo' ? (
                <img src={toWebUrl(selected.url)} alt={selected.fileName} style={{ width: '100%', borderRadius: 8, maxHeight: 500, objectFit: 'contain', backgroundColor: '#000' }} />
              ) : (
                <video src={toWebUrl(selected.url)} controls style={{ width: '100%', borderRadius: 8, maxHeight: 500 }} />
              )}
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>{selected.fileName}</div>
              <div style={s.detailRow}><span style={s.label}>유형</span><span>{selected.type === 'photo' ? '사진' : '동영상'}</span></div>
              <div style={s.detailRow}><span style={s.label}>크기</span><span>{formatSize(selected.fileSize)}</span></div>
              <div style={s.detailRow}><span style={s.label}>업로더</span><span>{selected.uploaderName}</span></div>
              <div style={s.detailRow}><span style={s.label}>분류</span><span>{MediaContexts[selected.context]?.label || selected.context}</span></div>
              <div style={s.detailRow}><span style={s.label}>날짜</span><span>{selected.createdAt?.toDate ? selected.createdAt.toDate().toLocaleDateString('ko-KR') : ''}</span></div>
              {selected.caption && <div style={{ marginTop: 12, fontSize: 13, color: '#AAB4C0' }}>{selected.caption}</div>}
              <div style={{ marginTop: 20, display: 'flex', gap: 8 }}>
                <a href={toWebUrl(selected.url)} target="_blank" rel="noopener noreferrer" style={s.dlBtn}>다운로드</a>
                {(canEdit || selected.uploadedBy === auth.currentUser?.uid) && (
                  <button onClick={() => handleDelete(selected.id)} style={s.delBtn}>삭제</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  panel: { backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(91,196,216,0.06)', borderRadius: 12, overflow: 'hidden' },
  panelHeader: { padding: '14px 20px', borderBottom: '1px solid rgba(91,196,216,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  panelTitle: { margin: 0, fontSize: 15, fontWeight: 600 },
  empty: { textAlign: 'center', padding: '60px 20px', color: '#556677', fontSize: 14 },
  select: { padding: '6px 10px', borderRadius: 6, border: '1px solid rgba(91,196,216,0.1)', backgroundColor: 'rgba(10,18,32,0.8)', color: '#E8ECF1', fontSize: 12 },
  uploadBtn: { padding: '8px 16px', borderRadius: 6, border: 'none', backgroundColor: '#5BC4D8', color: '#0A1220', fontSize: 12, fontWeight: 700, cursor: 'pointer' },
  closeBtn: { background: 'none', border: 'none', color: '#778899', fontSize: 18, cursor: 'pointer' },
  detailRow: { display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid rgba(91,196,216,0.04)', fontSize: 13 },
  label: { color: '#667788' },
  dlBtn: { padding: '8px 16px', borderRadius: 6, border: '1px solid rgba(91,196,216,0.2)', backgroundColor: 'transparent', color: '#5BC4D8', fontSize: 12, cursor: 'pointer', textDecoration: 'none', display: 'inline-block' },
  delBtn: { padding: '8px 16px', borderRadius: 6, border: '1px solid rgba(248,113,113,0.3)', backgroundColor: 'transparent', color: '#F87171', fontSize: 12, cursor: 'pointer' },
};
